import React, { useState } from 'react';
import {
  ZanaIntent,
  ZanaIntentExecutor,
  intentNeedsConfirm,
  labelForZanaIntent,
} from '../services/zanaActions';

/* Shared action chips for Ask dock, health banner, and insight bands.
   Navigate/read run on first tap. Writes arm Confirm / Cancel first. */

export default function ZanaActionChips({
  intents,
  onExecuteIntent,
  tone = 'dark',
  onResult,
}: {
  intents: ZanaIntent[];
  onExecuteIntent?: ZanaIntentExecutor;
  /** dark = purple dock/banner; light = white analytics bands */
  tone?: 'dark' | 'light';
  /**
   * Where the executor's receipt goes.
   *
   * `ZanaIntentExecutor` returns an owner-readable line on every branch —
   * "Flagged 2 buses on High Risk Alerts…", "Parked KDB123…", "Closed out 4
   * incident report(s)…" — and this component threw it away. The two chat
   * surfaces render it as a Zana message; the dashboard's summary card and
   * insight bands, which share this component, showed nothing at all. So a
   * write ran, changed data, and gave the owner no evidence it had happened:
   * "dashboard chips fire ops silently".
   */
  onResult?: (line: string) => void;
}) {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [runningKey, setRunningKey] = useState<string | null>(null);
  if (!onExecuteIntent || !intents.length) return null;

  const dark = tone === 'dark';
  const navigateBorder = dark ? '1px solid rgba(155,124,255,.45)' : '1px solid rgba(111,79,240,.35)';
  const navigateBg = dark ? 'rgba(155,124,255,.18)' : 'rgba(111,79,240,.08)';
  const navigateColor = dark ? '#efe9ff' : '#5a34c9';
  const writeBorder = dark ? '1px solid rgba(255,180,84,.5)' : '1px solid rgba(217,119,6,.45)';
  const writeBg = dark ? 'rgba(255,159,28,.14)' : 'rgba(255,159,28,.12)';
  const writeColor = dark ? '#ffe9cf' : '#9a3412';

  async function runIntent(intent: ZanaIntent, key: string) {
    setConfirmKey(null);
    setRunningKey(key);
    try {
      const result = await onExecuteIntent?.(intent);
      if (typeof result === 'string' && result) onResult?.(result);
    } catch {
      onResult?.("That didn't go through — try again in a moment.");
    } finally {
      setRunningKey(null);
    }
  }

  function onChipTap(intent: ZanaIntent, key: string) {
    if (intentNeedsConfirm(intent)) {
      setConfirmKey((k) => (k === key ? null : key));
      return;
    }
    void runIntent(intent, key);
  }

  return (
    <div data-zt="doChip" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {intents.map((intent, j) => {
        const key = `${intent.actionId}-${j}`;
        const needsConfirm = intentNeedsConfirm(intent);
        const arming = confirmKey === key;
        const running = runningKey === key;
        if (arming) {
          return (
            <span key={key} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => void runIntent(intent, key)}
                style={{
                  border: writeBorder,
                  background: dark ? 'rgba(255,159,28,.25)' : 'rgba(255,159,28,.2)',
                  color: writeColor,
                  borderRadius: 999,
                  padding: '5px 10px',
                  fontSize: 11.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Confirm: {labelForZanaIntent(intent).toLowerCase()} ✓
              </button>
              <button
                type="button"
                onClick={() => setConfirmKey(null)}
                style={{
                  border: dark ? '1px solid rgba(255,255,255,.25)' : '1px solid #cbd5e1',
                  background: 'transparent',
                  color: dark ? '#c9c0e8' : '#64748b',
                  borderRadius: 999,
                  padding: '5px 10px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </span>
          );
        }
        return (
          <button
            key={key}
            type="button"
            disabled={running}
            onClick={() => onChipTap(intent, key)}
            style={{
              border: needsConfirm ? writeBorder : navigateBorder,
              background: needsConfirm ? writeBg : navigateBg,
              color: needsConfirm ? writeColor : navigateColor,
              borderRadius: 999,
              padding: '5px 10px',
              fontSize: 11.5,
              fontWeight: 700,
              cursor: running ? 'wait' : 'pointer',
              opacity: running ? 0.6 : 1,
            }}
          >
            {running ? 'Working…' : `${labelForZanaIntent(intent)} ${needsConfirm ? '⚠' : '→'}`}
          </button>
        );
      })}
    </div>
  );
}
